/* eslint-disable react/no-unused-state */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { merge, noop, isEqual } from 'lodash';
import { ContentZonesService, LocaleService, UrlService, ConfigsModel } from '@sm360/widget-commons-core-services';
import {
    REDIRECTIONS,
    ReduxClient,
    Translations as GeneralTranslations,
    SessionStorageService,
} from '@sm360/widget-commons-general';
import { Translations as FormTranslations } from '@sm360/widget-commons-forms';
import { Vehicle, ShowroomService, ORGANIZATIONS } from '@sm360/widget-commons-vehicle';
import { Provider } from 'react-redux';
import envConfig from 'trade-in-360/config/index.js';

import en from 'trade-in-360/i18n/en.json';
import fr from 'trade-in-360/i18n/fr.json';

import InstantEstimate from 'trade-in-360/components/InstantEstimate/InstantEstimate.jsx';
import WIDGET_VERSION from 'trade-in-360/constants/widgetVersions.js';
import { NewTradeInProvider } from './NewTradeInContext';

class InstantEstimateProvider extends Component {
    constructor(props) {
        super(props);
        merge(en, FormTranslations.en, GeneralTranslations.en);
        merge(fr, FormTranslations.fr, GeneralTranslations.fr);

        if (props.locale) LocaleService.setLocale(props.locale);
        LocaleService.loadTranslations({ en, fr });
        const userLocationSessionData = SessionStorageService?.get('currentLocationData');
        const sessionOrgUnitLocation =
            props.isMultiDealerTradeIn && userLocationSessionData && userLocationSessionData?.stateCode;

        this.configsModel = new ConfigsModel({
            organizationId: props.organizationId,
            organizationUnitId: props.organizationUnitId,
            organizationUnitLocation: sessionOrgUnitLocation || props.organizationUnitLocation,
            websiteId: props.websiteId,
            location: props.location,
            organizationUnitName: props.organizationUnitName,
            organizationUnitPhone: props.organizationUnitPhone,
            schedule_monday: props.schedule_monday,
            schedule_tuesday: props.schedule_tuesday,
            schedule_wednesday: props.schedule_wednesday,
            schedule_thursday: props.schedule_thursday,
            schedule_friday: props.schedule_friday,
            schedule_saturday: props.schedule_saturday,
            schedule_sunday: props.schedule_sunday,
            salesDepartments: props.salesDepartments,
            websiteLogo: props.websiteLogo,
            websiteDomain: props.websiteDomain,
            websiteFacebookPageUrl: props.websiteFacebookPageUrl,
            websiteInstagramClientId: props.websiteInstagramClientId,
            websiteInstagramUserId: props.websiteInstagramUserId,
            websiteTwitterUrl: props.websiteTwitterUrl,
            websiteYoutubeUrl: props.websiteYoutubeUrl,
        });

        ContentZonesService.setImageServerBaseUrl(envConfig.source.image);
        if (props.contentZones) ContentZonesService.addContentZones(props.contentZones);

        this.storeRedux = ReduxClient.getOrCreate();
        ReduxClient.addReducers({
            tradeInVehicle: Vehicle.TradeInVehicleReducer,
        });

        this.loadQuote = this.loadQuote.bind(this);

        const urlVersion = parseInt(UrlService.getUrlParameter('version'), 10);
        const isDilawri = this.props.organizationId === ORGANIZATIONS.DILAWRI;
        const version = urlVersion || (isDilawri ? WIDGET_VERSION.DILAWRI : WIDGET_VERSION.TRADE_360);

        const cz = ContentZonesService.getContentZone('widget.tradein', 'hascbb');
        // For standalone tradeIn isShowroomV2Enabled and hascbb both would be false
        // But we need to present instant estimate, so this will trigger hasCBB true all the time for stand alone trade widget
        const hasCBB = !this.props.isShowroomV2Enabled || (cz ? cz.getValue() === 'true' : false);

        const viewParam = UrlService.getUrlParameter('view');
        const isOfficialTradeIn = ['official', 'officiel'].includes(viewParam);
        const isTradeInAppointment = ['appointment', 'rendez-vous'].includes(viewParam);
        const isViewOnly = ['view', 'voir'].includes(viewParam);

        // Prevent loading the trade-in saved in account
        const isNewRequest = UrlService.getUrlParameter('new') === 'true';
        const skipEstimate = UrlService.getUrlParameter('noEstimate') === 'true';
        const officialEdit = UrlService.getUrlParameter('officialEdit') === 'true';

        this.state = {
            quote: this.props.quote,
            fieldsLocked: isViewOnly,
            setFieldsLocked: this.setFieldsLocked,
            isOfficialTradeIn: isViewOnly || isOfficialTradeIn,
            setIsOfficialTradeIn: this.setIsOfficialTradeIn,
            isLoginModalOpen: false,
            setIsLoginModalOpen: this.setIsLoginModalOpen,
            isModified: false,
            setIsModified: this.setIsModified,
            useCBB: hasCBB,
            skipEstimate,
            version,
            isTradeInAppointment,
            isNewRequest,
            setNewRequest: this.setNewRequest,
            isViewOnly,
            officialEdit,
            setOfficialEdit: this.setOfficialEdit,
        };
    }

    componentDidMount() {
        if (!this.props.quote) {
            const quoteId = UrlService.getUrlParameter('quoteId');
            if (quoteId) {
                this.loadQuote(quoteId);
            }
        }
    }

    componentWillReceiveProps(newProps) {
        if (!isEqual(newProps.quote, this.props.quote)) {
            this.setState({
                quote: newProps.quote,
            });
        }
    }

    setFieldsLocked = (locked) => {
        this.setState({ fieldsLocked: locked });
    };

    setIsOfficialTradeIn = (isOfficial) => {
        if (isOfficial)
            UrlService.addOrReplaceParameter(
                'view',
                LocaleService.getSimpleLocale() === 'fr' ? 'officiel' : 'official'
            );
        else UrlService.removeParameter('view');
        this.setState({ isOfficialTradeIn: isOfficial });
    };

    setIsLoginModalOpen = (isOpen) => {
        this.setState({ isLoginModalOpen: isOpen });
    };

    setNewRequest = (isNewRequest) => {
        if (isNewRequest) UrlService.addOrReplaceParameter('new', 'true');
        else UrlService.removeParameter('new');
        this.setState({ isNewRequest });
    };

    setIsModified = (isModified) => {
        this.setState({ isModified: !!isModified });
    };

    setOfficialEdit = (officialEdit) => {
        if (officialEdit) UrlService.addOrReplaceParameter('officialEdit', 'true');
        else UrlService.removeParameter('officialEdit');
        this.setState({ officialEdit });
    };

    loadQuote(quoteId) {
        return ShowroomService.loadQuote(quoteId)
            .then((quote) => {
                this.setState({ quote });
            })
            .catch(noop);
    }

    render() {
        // When dealer does not have Showroom V2 we always want the modal version
        // Stand-alone should only show for when it's in the appraisal url
        const isStandAlone = UrlService.containsPath(REDIRECTIONS[LocaleService.getSimpleLocale()].tradeIn);
        const allowModal = isStandAlone ? false : this.props.isModal;
        const shouldBeModal = !this.props.isShowroomV2Enabled || allowModal;

        return (
            <Provider store={this.storeRedux}>
                <NewTradeInProvider value={this.state}>
                    <InstantEstimate
                        {...this.props}
                        quote={this.state.quote}
                        parentElementId={this.props.parentElementId || this.props.location}
                        isShowroomV2Enabled={this.props.isShowroomV2Enabled}
                        isModal={shouldBeModal}
                        config={this.configsModel.getConfig()}
                    />
                </NewTradeInProvider>
            </Provider>
        );
    }
}

InstantEstimateProvider.displayName = 'InstantEstimateProvider';

InstantEstimateProvider.defaultProps = {
    isModal: true,
    onTradeInSuccess: null,
    appointmentEnabled: true,
    isBarWrapper: false,
    isMultiDealerTradeIn: false,
};

InstantEstimateProvider.propTypes = {
    locale: PropTypes.string.isRequired,
    location: PropTypes.string.isRequired,
    websiteId: PropTypes.number.isRequired,
    organizationUnitId: PropTypes.number.isRequired,
    organizationId: PropTypes.number.isRequired,
    organizationUnitLocation: PropTypes.string.isRequired,
    contentZones: PropTypes.objectOf(
        PropTypes.arrayOf(
            PropTypes.shape({
                active: PropTypes.bool,
                group: PropTypes.string,
                id: PropTypes.number,
                location: PropTypes.string,
                position: PropTypes.number,
                type: PropTypes.string,
                value: PropTypes.string,
            })
        )
    ).isRequired,
    isShowroomV2Enabled: PropTypes.bool.isRequired,
    isModal: PropTypes.bool.isRequired,
    onTradeInSuccess: PropTypes.func, // called when cleanly ending the tradein flow
    appointmentEnabled: PropTypes.bool,
    isBarWrapper: PropTypes.bool,
    isMultiDealerTradeIn: PropTypes.bool,
};

export default InstantEstimateProvider;
